import { create } from "zustand";
import { RepositoryManager } from "../../data/repositoryManager";
import { message } from "antd";

export interface Notification {
  id: number;
  title: string;
  content: string;
  type?: string;
  user_id?: number | null;
  user?: { id: number; name: string; email: string } | null;
  is_broadcast: boolean;
  created_at: string;
  updated_at?: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  username?: string;
  phone_number?: string;
  role?: string;
  is_active?: boolean;
  is_blocked?: boolean;
}

interface NotificationsState {
  notificationsData: any | null;
  notificationDetail: Notification | null;
  isListLoading: boolean;
  isDetailLoading: boolean;
  isSaving: boolean;
  
  // Users state
  usersList: User[];
  isLoadingUsers: boolean;

  getNotifications: (params?: any) => Promise<any>;
  getNotificationById: (id: number) => Promise<any>;
  sendToUser: (payload: {
    user_id: number;
    title: string;
    message: string;
    type?: string;
    data?: any;
    push?: boolean;
  }) => Promise<any>;
  sendToUsers: (payload: {
    user_ids: number[];
    title: string;
    message: string;
    type?: string;
    data?: any;
    push?: boolean;
  }) => Promise<any>;
  broadcast: (payload: {
    title: string;
    message: string;
    type?: string;
    data?: any;
    push?: boolean;
  }) => Promise<any>;
  deleteNotification: (id: number) => Promise<any>;
  loadUsers: (search?: string) => Promise<void>;
  clearUsersList: () => void;
}

const useNotificationsStore = create<NotificationsState>((set) => ({
  notificationsData: null,
  notificationDetail: null,
  isListLoading: false,
  isDetailLoading: false,
  isSaving: false,
  usersList: [],
  isLoadingUsers: false,

  async getNotifications(params = {}) {
    set({ isListLoading: true });
    try {
      const res = await RepositoryManager.notificationsAdmin.getNotifications(params);
      if (res.success) {
        set({ notificationsData: res.data });
      }
      return res;
    } finally {
      set({ isListLoading: false });
    }
  },

  async getNotificationById(id) {
    set({ isDetailLoading: true });
    try {
      const res = await RepositoryManager.notificationsAdmin.getDetail(id);
      if (res.success) {
        set({ notificationDetail: res.data });
      }
      return res;
    } finally {
      set({ isDetailLoading: false });
    }
  },

  async sendToUser(payload) {
    set({ isSaving: true });
    try {
      const res = await RepositoryManager.notificationsAdmin.sendToUser(payload);
      return res;
    } finally {
      set({ isSaving: false });
    }
  },

  async sendToUsers(payload) {
    set({ isSaving: true });
    try {
      const res = await RepositoryManager.notificationsAdmin.sendToUsers(payload);
      return res;
    } finally {
      set({ isSaving: false });
    }
  },

  async broadcast(payload) {
    set({ isSaving: true });
    try {
      const res = await RepositoryManager.notificationsAdmin.broadcast(payload);
      return res;
    } finally {
      set({ isSaving: false });
    }
  },

  async deleteNotification(id) {
    set({ isSaving: true });
    try {
      const res = await RepositoryManager.notificationsAdmin.delete(id);
      return res;
    } finally {
      set({ isSaving: false });
    }
  },

  async loadUsers(search?: string) {
    set({ isLoadingUsers: true });
    try {
      const params: any = {
        per_page: 50,
      };
      if (search && search.length >= 2) {
        params.search = search;
      }
      const res = await RepositoryManager.users.getUsers(params);
      console.log("result ", res);
      if (res.success && res.data) {
        // Handle different response structures
        let usersArray: User[] = [];
        if (res.data.users && Array.isArray(res.data.users)) {
          // Structure: { success: true, data: { users: [...], pagination: {...} } }
          usersArray = res.data.users;
        } else if (res.data.data?.users && Array.isArray(res.data.data.users)) {
          // Structure: { data: { data: { users: [...], pagination: {...} } } }
          usersArray = res.data.data.users;
        } else if (Array.isArray(res.data.data)) {
          // Structure: { data: { data: [...] } }
          usersArray = res.data.data;
        } else if (Array.isArray(res.data)) {
          // Structure: { data: [...] } (direct array)
          usersArray = res.data;
        }
        console.log("usersArray", usersArray);
        set({ usersList: usersArray });
      }
    } catch (error) {
      console.error("Error loading users:", error);
      message.error("Không thể tải danh sách users");
      set({ usersList: [] });
    } finally {
      set({ isLoadingUsers: false });
    }
  },

  clearUsersList: () => {
    set({ usersList: [] });
  },
}));

export default useNotificationsStore;

